/**
 * Product Card Video — Desktop-only, lazy load with IntersectionObserver
 *
 * Phase 1 (IO): When a card enters viewport (+200px margin), create a hidden
 *         <video> with preload="metadata" — downloads only headers (~few KB).
 * Phase 2 (mouseenter): Switch to preload="auto", show video, play().
 *         (mouseleave): Pause, hide video, restore poster.
 *
 * Mobile (≤1024px): No JS execution at all. Videos hidden via CSS.
 */

const handleProductCardVideo = () => {
  const MOBILE_BP = 1024;
  const INIT_ATTR = 'data-video-init';

  const isMobile = () => window.innerWidth <= MOBILE_BP;

  /**
   * Check if this card has gender-swapped images for the current collection.
   * If so, skip video — it would show the wrong gender.
   */
  const isGenderSwapped = (card) => {
    const section = document.querySelector('.section-collection');
    if (!section) return false;
    if (!section.contains(card)) return false;
    const collectionId = (section.dataset.id || '').trim();
    if (!collectionId) return false;

    const menIds = (card.dataset.menCollection || '').split(',').map(s => s.trim()).filter(Boolean);
    const womenIds = (card.dataset.womenCollection || '').split(',').map(s => s.trim()).filter(Boolean);

    return (menIds.includes(collectionId) && card.dataset.menImgs) ||
           (womenIds.includes(collectionId) && card.dataset.womenImgs);
  };

  /**
   * Build a <video> or <iframe> from placeholder data attributes.
   * Returns the element (not yet inserted into DOM) or null.
   */
  const buildVideoEl = (ph) => {
    const type = ph.dataset.videoType;

    if (type === 'native') {
      const el = document.createElement('video');
      el.muted = true;
      el.loop = true;
      el.playsInline = true;
      el.setAttribute('playsinline', '');
      el.controls = false;
      el.preload = 'metadata';
      el.style.display = 'none';

      const json = ph.getAttribute('data-video-sources');
      if (json) {
        try {
          const sources = JSON.parse(json);
          for (const s of sources) {
            const src = s.url || s.src || '';
            if (!src) continue;
            const srcEl = document.createElement('source');
            srcEl.src = src;
            srcEl.type = s.mime_type || 'video/mp4';
            el.appendChild(srcEl);
          }
        } catch (e) {}
      }
      return el;
    }

    if (type === 'external') {
      const src = ph.dataset.videoSrc;
      if (!src) return null;
      const el = document.createElement('iframe');
      el.src = src;
      el.className = `js-${ph.dataset.videoHost || ''}`;
      el.setAttribute('loading', 'lazy');
      el.setAttribute('allow', 'autoplay; encrypted-media');
      el.setAttribute('allowfullscreen', '');
      el.frameBorder = '0';
      el.style.display = 'none';
      return el;
    }

    return null;
  };

  /**
   * Find the video/iframe element associated with a placeholder.
   * Returns the element or null.
   */
  const findVideoEl = (ph) => {
    const prev = ph.previousElementSibling;
    return prev && (prev.tagName === 'VIDEO' || prev.tagName === 'IFRAME') ? prev : null;
  };

  /**
   * Phase 1 — preload: insert hidden video elements so browser starts buffering
   */
  const preloadCard = (card) => {
    if (card.getAttribute(INIT_ATTR) === 'preloaded') return;
    if (isGenderSwapped(card)) return;

    const ph = card.querySelector('.product-card-video-placeholder');
    if (!ph) return;

    const el = buildVideoEl(ph);
    if (!el) return;

    ph.parentNode.insertBefore(el, ph);
    card.setAttribute(INIT_ATTR, 'preloaded');
  };

  /**
   * Phase 2 — hover handlers
   */
  const setupHover = (card) => {
    if (card.hasAttribute('data-video-hover')) return;
    card.setAttribute('data-video-hover', '1');

    card.addEventListener('mouseenter', () => {
      if (isMobile()) return;
      if (isGenderSwapped(card)) return;

      const ph = card.querySelector('.product-card-video-placeholder');
      if (!ph) return;

      // Find existing preloaded video, or build on-demand
      let videoEl = findVideoEl(ph);
      if (!videoEl) {
        videoEl = buildVideoEl(ph);
        if (!videoEl) return;
        ph.parentNode.insertBefore(videoEl, ph);
        card.setAttribute(INIT_ATTR, 'preloaded');
      }

      videoEl.style.display = '';
      if (videoEl.tagName === 'VIDEO') {
        videoEl.preload = 'auto';
        videoEl.autoplay = true;
        videoEl.play().catch(() => {});
      }

      const poster = card.querySelector('.product-card-poster');
      if (poster) poster.style.opacity = '0';
    });

    card.addEventListener('mouseleave', () => {
      const ph = card.querySelector('.product-card-video-placeholder');
      if (!ph) return;

      const videoEl = findVideoEl(ph);
      if (!videoEl) return;

      const poster = card.querySelector('.product-card-poster');
      if (poster) poster.style.opacity = '';

      if (videoEl.tagName === 'VIDEO') {
        videoEl.pause();
        videoEl.style.display = 'none';
      } else if (videoEl.tagName === 'IFRAME') {
        videoEl.remove();
        card.setAttribute(INIT_ATTR, '');
      }
    });
  };

  /**
   * IntersectionObserver — preload metadata only when card is near viewport
   */
  const ioOptions = { rootMargin: '200px' };
  let cardObserver = null;

  const getCardObserver = () => {
    if (cardObserver) return cardObserver;
    cardObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          preloadCard(entry.target);
          cardObserver.unobserve(entry.target);
        }
      }
    }, ioOptions);
    return cardObserver;
  };

  const initProductCardVideos = () => {
    if (isMobile()) return;
    const obs = getCardObserver();
    document.querySelectorAll('.snippet-product-grid').forEach((card) => {
      setupHover(card);
      if (card.getAttribute(INIT_ATTR) !== 'preloaded') {
        obs.observe(card);
      }
    });
  };

  // Boot after window load (page fully rendered first)
  if (document.readyState === 'complete') {
    initProductCardVideos();
  } else {
    window.addEventListener('load', initProductCardVideos);
  }

  // MutationObserver for infinite scroll / swatch swaps
  let debounceTimer = null;
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.addedNodes.length > 0) {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(initProductCardVideos, 200);
        return;
      }
    }
  });

  const startObserving = () => {
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }

  // Expose for manual calls (swatches, etc.)
  window.initProductCardVideos = initProductCardVideos;
};

handleProductCardVideo();
